/**
 * TeamSLAMonitor — manager dashboard panel for team SLA performance.
 *
 * Five headline metrics (Missed Per Agent / Team Compliance % / Avg
 * Response / Critical Queue / Rotation Volume) plus a sortable agent
 * ranking table — most-missed first by default, per spec.
 *
 * Data source: GET /api/dashboard/manager-sla (Phase 3, gated to
 * manager / HOD / CEO / admin).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Skeleton } from './ui/skeleton';
import {
  Activity, AlertTriangle, BarChart3, CheckCircle2, Clock, Download,
  RefreshCw, ShieldAlert, Users,
} from 'lucide-react';
import HelpInfo from './HelpInfo';

const API = process.env.REACT_APP_BACKEND_URL;

const PERIODS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
];

const SORTS = [
  { value: 'missed', label: 'Most missed' },
  { value: 'compliance_asc', label: 'Lowest compliance' },
  { value: 'attended', label: 'Most attended' },
  { value: 'volume', label: 'Most active' },
  { value: 'name', label: 'Name (A→Z)' },
];

export default function TeamSLAMonitor() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [period, setPeriod] = useState('today');
  const [sortBy, setSortBy] = useState('missed');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/dashboard/manager-sla`, {
        params: { token, period },
      });
      setData(res.data?.data || null);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token, period]);

  useEffect(() => { load(); }, [load]);

  const team = data?.team;
  const agents = data?.per_agent || [];

  // Client-side re-sort — the API returns most-missed by default, but
  // the manager may want a different angle.
  const ranked = useMemo(() => {
    const arr = [...agents];
    const cmp = {
      missed: (a, b) => b.missed - a.missed || b.rotation_events - a.rotation_events,
      compliance_asc: (a, b) => a.compliance_pct - b.compliance_pct || b.missed - a.missed,
      attended: (a, b) => b.attended - a.attended,
      volume: (a, b) => b.rotation_events - a.rotation_events,
      name: (a, b) => (a.agent_name || '').localeCompare(b.agent_name || ''),
    }[sortBy] || ((a, b) => b.missed - a.missed);
    arr.sort(cmp);
    return arr;
  }, [agents, sortBy]);

  const complianceTone = (pct) => {
    if (pct >= 90) return 'text-emerald-600';
    if (pct >= 70) return 'text-amber-600';
    return 'text-red-600';
  };

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2 flex-wrap">
          <Users size={16} className="text-violet-500" />
          Team SLA Monitor
          <HelpInfo
            align="inline"
            title="How to read Team SLA"
            steps={[
              <>The five tiles up top are the team's headline numbers in the chosen window — total attended, total missed, average response time on attended leads, the critical queue depth, and total rotations (assignments + reassignments).</>,
              <>The ranking table lists every agent active in the window. By default <strong>most-missed first</strong> — the agents to coach. Switch the sort to surface different angles (lowest compliance, most attended, most active).</>,
              <>A row's <strong>compliance</strong> is Attended ÷ (Attended + Missed). Green ≥ 90%, amber 70–90%, red below.</>,
              <>The <strong>Critical queue</strong> tile is leads that rotated through the whole pool with no contact. Open <em>/leads-critical</em> to reassign manually.</>,
            ]}
            tip="If the critical queue grows faster than your team can drain it, the rotation cap or the pool size is too small — adjust in SLA settings."
          />
          <span className="ml-auto flex items-center gap-2">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="h-8 w-[140px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIODS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              type="button"
              onClick={() => window.open(
                `${API}/api/dashboard/manager-sla/export.csv?token=${encodeURIComponent(token)}&period=${period}`,
                '_blank',
              )}
              className="inline-flex items-center gap-1 h-8 px-2 rounded text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              title="Export CSV"
            >
              <Download size={12} /> CSV
            </button>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="inline-flex items-center justify-center w-7 h-7 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100"
              title="Refresh"
            >
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            </button>
          </span>
        </CardTitle>
        <CardDescription>
          SLA compliance and rotation activity across the team.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Team headline tiles */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <Tile loading={loading && !team} icon={CheckCircle2} tone="text-emerald-500"
                label="Attended" value={team?.attended ?? 0} />
          <Tile loading={loading && !team} icon={AlertTriangle} tone="text-red-500"
                label="Missed" value={team?.missed ?? 0} />
          <Tile loading={loading && !team} icon={Activity}
                valueClass={complianceTone(team?.compliance_pct ?? 100)}
                label="Team compliance" value={`${team?.compliance_pct ?? 0}%`} />
          <Tile loading={loading && !team} icon={Clock} tone="text-amber-500"
                label="Avg response"
                value={team?.avg_response_minutes != null
                  ? `${team.avg_response_minutes}m`
                  : '—'} />
          <Tile loading={loading && !team} icon={ShieldAlert} tone="text-rose-500"
                label="Critical queue"
                value={team?.critical_queue ?? 0}
                onClick={() => navigate('/contacts?critical=true')} />
        </div>

        {/* Rotation volume note */}
        {team?.rotation_volume != null && (
          <div className="text-xs text-slate-500 flex items-center gap-1.5">
            <BarChart3 size={11} />
            <strong>{team.rotation_volume}</strong> total rotation events this window.
          </div>
        )}

        {/* Agent ranking */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
              Agent ranking
            </p>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-7 w-[180px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORTS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {loading && !agents.length ? (
            <Skeleton className="h-32 w-full" />
          ) : ranked.length === 0 ? (
            <p className="text-xs text-slate-400 italic text-center py-4">
              No agent SLA activity in this window.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-1.5 px-2">#</th>
                    <th className="text-left py-1.5 px-2">Agent</th>
                    <th className="text-center py-1.5 px-2">Attended</th>
                    <th className="text-center py-1.5 px-2">Missed</th>
                    <th className="text-center py-1.5 px-2">Events</th>
                    <th className="text-right py-1.5 px-2">Compliance</th>
                  </tr>
                </thead>
                <tbody>
                  {ranked.map((a, i) => (
                    <tr
                      key={a.agent_id}
                      className="border-b border-slate-100 hover:bg-slate-50"
                    >
                      <td className="py-1.5 px-2 text-slate-400 font-mono text-xs">{i + 1}</td>
                      <td className="py-1.5 px-2 font-medium truncate max-w-[200px]">
                        {a.agent_name}
                      </td>
                      <td className="py-1.5 px-2 text-center text-emerald-600">
                        {a.attended}
                      </td>
                      <td className="py-1.5 px-2 text-center">
                        {a.missed > 0 ? (
                          <Badge className="text-[10px] bg-red-100 text-red-700 border border-red-300">
                            {a.missed}
                          </Badge>
                        ) : <span className="text-slate-300">0</span>}
                      </td>
                      <td className="py-1.5 px-2 text-center text-slate-500">
                        {a.rotation_events}
                      </td>
                      <td className={`py-1.5 px-2 text-right font-semibold ${complianceTone(a.compliance_pct)}`}>
                        {a.compliance_pct}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

const Tile = ({ icon: Icon, tone, label, value, valueClass, loading, onClick }) => {
  const inner = (
    <>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-slate-500">
        {Icon && <Icon size={11} className={tone} />}
        {label}
      </div>
      {loading ? (
        <Skeleton className="h-6 w-12 mt-1" />
      ) : (
        <div className={`text-xl font-bold mt-0.5 ${valueClass || 'text-slate-900'}`}>
          {value}
        </div>
      )}
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="rounded-md border border-slate-200 p-2.5 text-left hover:bg-slate-50 transition"
      >
        {inner}
      </button>
    );
  }
  return (
    <div className="rounded-md border border-slate-200 p-2.5">
      {inner}
    </div>
  );
};
